// Pipeline Tracker: reading and editing the tenant's monthly ad spend.
//
// Same contract as useBrokerRoster, deliberately: the server takes the WHOLE entry list plus the
// version the client loaded, so two admins editing at once means the second save is refused
// rather than silently erasing a month the first one entered.

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { TAXONOMY_CALLABLE, GET_AD_SPEND_ACTION, SAVE_AD_SPEND_ACTION } from './reportContract';
import type { AdSpendEntry } from './adSpendRules';
import { invalidateTrackerReports } from './reportCache';

interface AdSpendDoc { entries: AdSpendEntry[]; version: number; updatedAt: string; updatedBy: string }

export interface AdSpendSaveOutcome { ok: boolean; conflict?: boolean; message?: string }

/**
 * @param enabled false for a viewer who may not edit, so the page makes no call that would be
 *                denied. The Marketing figures themselves come from the report, not from here.
 */
export function useAdSpend(companyId: string | undefined, enabled: boolean) {
  const [doc, setDoc] = useState<AdSpendDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!companyId || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, TAXONOMY_CALLABLE);
      const res = (await call({ action: GET_AD_SPEND_ACTION, companyId })) as HttpsCallableResult<{
        adSpend: AdSpendDoc; isSeed: boolean; invalidStoredDocument?: string;
      }>;
      setDoc(res.data.adSpend);
      if (res.data.invalidStoredDocument) {
        setError('The saved ad spend could not be read. Saving now replaces it with what is shown here.');
      }
    } catch (err) {
      console.error('[PipelineTracker] getAdSpend failed', err);
      setError('Ad spend could not be loaded, so it cannot be edited right now.');
    } finally {
      setLoading(false);
    }
  }, [companyId, enabled]);

  useEffect(() => { void load(); }, [load]);

  const save = useCallback(async (entries: AdSpendEntry[]): Promise<AdSpendSaveOutcome> => {
    if (!companyId || !doc) return { ok: false, message: 'Not ready.' };
    setSaving(true);
    try {
      const call = httpsCallable(functions, TAXONOMY_CALLABLE);
      const res = (await call({
        action: SAVE_AD_SPEND_ACTION, companyId, entries, expectedVersion: doc.version,
      })) as HttpsCallableResult<{ version: number }>;
      setDoc({ ...doc, entries, version: res.data.version });
      invalidateTrackerReports();
      return { ok: true };
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      if (code.endsWith('aborted')) {
        console.error('[PipelineTracker] saveAdSpend version conflict', err);
        void load();
        return { ok: false, conflict: true, message: 'Someone else changed the ad spend while this page was open. Reloaded; check it and try again.' };
      }
      console.error('[PipelineTracker] saveAdSpend failed', err);
      return { ok: false, message: 'That change could not be saved. Nothing was changed.' };
    } finally {
      setSaving(false);
    }
  }, [companyId, doc, load]);

  return { entries: doc?.entries ?? [], ready: doc !== null, loading, error, saving, save, reload: load };
}
